import React, { useRef } from 'react';
import { useIntl } from 'umi';
import { inject, observer } from 'mobx-react';
import RTCModel from '@/models/rtc';
import Style from './index.less';
import { Icon, Switch, Tooltip } from '@/components';
import Tip from '@/pages/Ready/component/Tip';
import MICSettingPanel from '../../SettingPanel/MICSettingPanel';
import VideoSettingPanel from '../../SettingPanel/VideoSettingPanel';

interface DevicePanelProps {
  mustCheck: boolean;
  checkMIC: () => Promise<void>;
  onMicSwitchChange: (value: boolean) => Promise<void>;
  RTCInstance?: RTCModel;
}

const DevicePanel: React.FC<DevicePanelProps> = inject(({ store }) => {
  return {
    RTCInstance: store.rtc,
    meeting: store.meeting,
    user: store.user,
  };
})(
  observer((props) => {
    const { mustCheck, checkMIC, onMicSwitchChange, RTCInstance } = props;
    const containerRef: any = useRef();
    const closedRef: any = useRef();
    const heightRef: any = useRef(0);
    const [isMICOptionVisible, setIsMICOptionVisible] = React.useState<boolean>(false);
    const [isVideoOptionVisible, setIsVideoOptionVisible] = React.useState<boolean>(false);
    const { formatMessage } = useIntl();

    const setVideoSize = React.useCallback(
      (cut = true) => {
        if (RTCInstance && RTCInstance.localStream && containerRef.current) {
          RTCInstance.localStream.setLocalRenderMode({
            width: containerRef.current.offsetWidth,
            height: heightRef.current ? heightRef.current : containerRef.current.offsetHeight,
            cut, // 是否裁剪
          });
        }
      },
      [RTCInstance, RTCInstance?.localStream, containerRef]
    );

    const onVideoSwitchChange = React.useCallback(
      async (value: boolean) => {
        if (value) {
          if (closedRef.current) {
            heightRef.current = closedRef.current.offsetHeight;
          }
          await RTCInstance?.openCamera(false);
          await RTCInstance?.playLocalVideoStream(containerRef.current);

          // 设置播放的视频容器大小
          setVideoSize();
        } else {
          await RTCInstance?.closeCamera();
          if (containerRef.current?.children?.[0]) {
            containerRef.current.innerHTML = '';
          }
        }
      },
      [RTCInstance, RTCInstance?.localStream, containerRef]
    );

    React.useEffect(() => {
      const resizeHandle = () => {
        // 设置播放的视频容器大小
        setVideoSize();
      };
      window.addEventListener('resize', resizeHandle);
      return () => {
        window.removeEventListener('resize', resizeHandle);
        if (RTCInstance && RTCInstance.localStream) {
          RTCInstance?.localStream.stop('video');
        }
      };
    }, []);

    React.useEffect(() => {
      const cb = () => {
        setIsMICOptionVisible(false);
        setIsVideoOptionVisible(false);
      };
      document.addEventListener('click', cb);
      RTCInstance?.getDeviceOptions(false).catch((e) => console.error(e.message));
      return () => {
        document.removeEventListener('click', cb);
      };
    }, []);

    return (
      <div className={Style.panel}>
        <div
          ref={containerRef}
          className={Style.container}
          style={RTCInstance?.isOpenVideo ? { maxHeight: heightRef.current } : { display: 'none' }}
        />
        {!RTCInstance?.isOpenVideo && (
          <div className={Style['closed-video']} ref={closedRef}>
            <Icon className={Style['dark-icon']} name="icon-video_noequipment" size={32} />
            <span className={Style['closed-video-tips']}>
              {formatMessage({ id: 'CAMERA_IS_OFF' })}
            </span>
          </div>
        )}
        <div className={Style.control}>
          <div className={Style.device}>
            <div
              className={Style.mic}
              onClick={
                window.isWeb ? undefined : () => onMicSwitchChange(!RTCInstance?.isOpenAudio)
              }
            >
              <div
                onClick={(e) => {
                  e.nativeEvent.stopImmediatePropagation();
                  setIsMICOptionVisible(!isMICOptionVisible);
                  setIsVideoOptionVisible(false);
                }}
              >
                {RTCInstance?.micIsNotAvailable ? (
                  <Tooltip title={formatMessage({ id: 'NO_DEVICE' })}>
                    <div className={Style.wrap}>
                      <Icon className={Style['dark-icon']} name="icon-micro_noequipment" size={20} />
                      <Icon
                        className={Style['warning-icon']}
                        mode="symbol"
                        name="icon-warming_solid"
                        size={16}
                      />
                    </div>
                  </Tooltip>
                ) : RTCInstance?.isOpenAudio ? (
                  <Icon className={Style['white-icon']} name="icon-micro_solid" size={20} />
                ) : (
                  <Icon className={Style['white-icon']} name="icon-micro_solid" size={20} />
                )}

                {window.isMobile && (
                  <span className={Style.micName}>{formatMessage({ id: 'MICROPHONE' })}</span>
                )}
                <Icon
                  className={Style['white-icon']}
                  name="icon-rough_arrow_up"
                  size={12}
                  style={isMICOptionVisible ? { marginBottom: '8px' } : {}}
                />
              </div>
              {window.isWeb && <MICSettingPanel visible={isMICOptionVisible} />}
              <Switch
                disabled={RTCInstance?.micIsNotAvailable}
                checked={RTCInstance?.isOpenAudio}
                className={Style['open-mic']}
                onChange={window.isWeb ? onMicSwitchChange : undefined}
                size="small"
              />
            </div>
            <div className={Style.line} />
            <div
              className={Style.video}
              onClick={
                window.isWeb ? undefined : () => onVideoSwitchChange(!RTCInstance?.isOpenVideo)
              }
            >
              <div
                onClick={(e) => {
                  e.nativeEvent.stopImmediatePropagation();
                  setIsVideoOptionVisible(!isVideoOptionVisible);
                  setIsMICOptionVisible(false);
                }}
              >
                {RTCInstance?.cameraIsNotAvailable ? (
                  <Tooltip title={formatMessage({ id: 'NO_DEVICE' })}>
                    <div className={Style.wrap}>
                      <Icon className={Style['dark-icon']} name="icon-video_noequipment" size={20} />
                      <Icon
                        className={Style['warning-icon']}
                        mode="symbol"
                        name="icon-warming_solid"
                        size={16}
                      />
                    </div>
                  </Tooltip>
                ) : (
                  <span className={Style.videoIconBox}>
                    <Tip
                      visible={!RTCInstance?.isOpenVideo}
                      content={formatMessage({ id: 'INTERVIEW_CAMERA_TIPS' })}
                      title={window.isMobile ? '' : formatMessage({ id: 'CAMERA' })}
                      isMobile={window.isMobile}
                    />
                    <Icon className={Style['white-icon']} name="icon-video_solid" size={20} />
                  </span>
                )}
                {window.isMobile && (
                  <span className={Style.micName}>{formatMessage({ id: 'CAMERA' })}</span>
                )}
                <Icon className={Style['white-icon']} name="icon-rough_arrow_up" size={12} />
              </div>

              {window.isWeb && <VideoSettingPanel visible={isVideoOptionVisible} />}
              <Switch
                disabled={RTCInstance?.cameraIsNotAvailable}
                checked={RTCInstance?.isOpenVideo}
                className={Style['open-video']}
                onChange={window.isWeb ? onVideoSwitchChange : undefined}
                size="small"
              />
            </div>
          </div>
          {window.isWeb && !mustCheck && (
            <div className={Style.devicecheck} onClick={checkMIC}>
              <span>{formatMessage({ id: 'CHECK_DEVICES' })}</span>
            </div>
          )}
        </div>
      </div>
    );
  })
);

export default DevicePanel;
